import { useState } from "react";
import { ExternalLink, Layers } from "lucide-react";
import { cn } from "@wa/ui";
import { groupByCategory, type Detection } from "../../../lib/siteScan";
import { Card, CardHead, SearchField, StatusPill } from "../../ui";
import type { T } from "./types";

/**
 * The technology inventory, grouped by what each thing is for.
 *
 * Two things make this more than a list of logos. Confidence is shown, so an
 * uncertain match reads as uncertain instead of as fact. And every card can be
 * opened to reveal the exact text that produced it - a claim on a sales call has
 * to be checkable in ten seconds, or it should not be made.
 */

/**
 * How sure the fingerprint is, as a bar.
 *
 * On the theme's success and warning colours. A certain match and an uncertain
 * one are the two things this says, and they are the same two things a status
 * pill says - the middle band used to be sky blue, which named a third state
 * the reader has no way to interpret.
 */
function ConfidenceBar({ value }: { value: number }) {
  const colour = value >= 100 ? "var(--color-success)" : value >= 60 ? "var(--color-primary)" : "var(--color-warning)";
  return (
    <span className="flex h-1 w-8 overflow-hidden rounded-full bg-[var(--crm-sunk)]" aria-hidden>
      <span
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.max(8, Math.min(100, value))}%`, background: colour }}
      />
    </span>
  );
}

function TechCard({ item, t }: { item: Detection; t: T }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-2xl bg-[var(--crm-sunk)] transition-shadow hover:shadow-[var(--crm-sh-sm)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{item.name}</span>
            {item.version && (
              <StatusPill tone="mut" className="shrink-0 font-mono">
                {item.version}
              </StatusPill>
            )}
          </span>
        </span>
        <ConfidenceBar value={item.confidence} />
      </button>

      {open && (
        <div className="border-t border-[var(--crm-hair)] px-3 py-2">
          <p className="text-[11px] font-medium text-muted-foreground">
            {t("scanner.confidence", { value: item.confidence })}
          </p>
          <ul className="mt-1.5 space-y-1">
            {item.evidence.map((e, i) => (
              <li key={`${e.source}-${i}`} className="break-all font-mono text-[11px] leading-relaxed">
                <span className="mr-1.5 rounded bg-[var(--crm-surface)] px-1 py-0.5 text-muted-foreground">{e.source}</span>
                {e.match}
              </li>
            ))}
            {item.evidence.length === 0 && (
              <li className="text-[11px] text-muted-foreground">{t("scanner.tech.noEvidence")}</li>
            )}
          </ul>
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
            >
              {t("scanner.tech.vendor")} <ExternalLink className="size-3" />
            </a>
          )}
        </div>
      )}
    </li>
  );
}

export function TechGrid({ technologies, t }: { technologies: Detection[]; t: T }) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? technologies.filter((d) => d.name.toLowerCase().includes(needle) || d.id.includes(needle))
    : technologies;
  const groups = groupByCategory(filtered);

  return (
    <Card>
      <CardHead
        title={
          <span className="flex items-center gap-2">
            <Layers className="size-4 text-primary" />
            {t("scanner.tech.title")}
            <span className="crm-tnum text-xs font-normal text-muted-foreground">({technologies.length})</span>
          </span>
        }
        actions={
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t("scanner.tech.filter")}
            className="w-48"
          />
        }
      />

      <div className="crm-scan-body">
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("scanner.tech.empty")}</p>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.category}>
                <p className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-muted-foreground">
                  {t(`scanner.category.${group.category}`)}
                </p>
                <ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map((item) => (
                    <TechCard key={item.id} item={item} t={t} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
